import * as React from 'react';
import { memo } from 'react';

import guid from 'helper/guid';
import noop from 'helper/noop';

import { actionCreators as connectionAC } from 'store/connection';

type AsyncComponentProps = {} & Object;

type AsyncComponentState<C> = {
	component: null | C;
};

export default function asyncComponent<C extends React.Component<any>>(
	componentName: string,
	rootStore: any,
	componentImporterFn: () => { default: C }
) {
	class AsyncComponent extends React.Component<AsyncComponentProps, AsyncComponentState<C>> {
		public static displayName = `AsyncComponent(${componentName})`;

		public state: AsyncComponentState<C> = {
			component: null,
		};

		public forceSetState: (newState: AsyncComponentState<C>, callback?: () => void) => void = undefined as any;

		public async fetchComponent(): Promise<{ default: C }> {
			return (await componentImporterFn()) as any;
		}

		public componentDidMount() {
			this.forceSetState = (newState: AsyncComponentState<C>, callback?: () => void) =>
				this.setState(newState, callback);

			this.fetchComponent()
				.then(({ default: component }) => {
					if (!component) {
						return;
					}

					this.forceSetState({
						component,
					});
				})
				.catch((err: Error) => {
					rootStore.dispatch(connectionAC.doTrackChunkLoadingError(err, componentName));
				});
		}

		public componentWillUnmount() {
			this.forceSetState = noop;
		}

		public render() {
			const SelectedComponent: any = this.state.component;
			const props = {
				...(this.props as any),
				key: `Dynamic${componentName}-${guid()}`,
				mountedAt: Date.now(),
			};

			return SelectedComponent ? <SelectedComponent {...(props as any)} /> : null;
		}
	}

	return memo(AsyncComponent);
}
